import { stringify as stringifyQuery } from 'query-string';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router';
import filesAPI from '../api/files';
import { FounderId } from '../api/founders';
import mentorsAPI, { Mentor } from '../api/mentors';
import venturesAPI from '../api/ventures';
import { VentureId } from '../api/ventures/types/Venture';
import { OptionProps } from '../components/common/select';
import { useQuery } from '../hooks/useQuery';
import { decodeQuery, encodeQuery } from '../utils/functions';
import { UserContext } from './user-context';

export interface MyMentorsFilters {
  venture?: VentureId;
}

export interface MyMentorsContextProps {
  mentors: Mentor[];
  mentorsLogos: { [x: string]: string | null };
  isLoading: boolean;
  isEmptyState: boolean;
  selectedVenture: VentureId | null;
  ventureOptions: OptionProps<VentureId>[];
  isVenturesLoading: boolean;
  writeReviewTargetMentor: Mentor | null;
  manageMentorTargetMentor: Mentor | null;
  loadError?: string;
  setMentors: Dispatch<SetStateAction<Mentor[]>>;
  setSelectedVenture: Dispatch<SetStateAction<VentureId | null>>;
  setWriteReviewTargetMentor: (mentor: Mentor | null) => void;
  setManageMentorTargetMentor: (mentor: Mentor | null) => void;
}

export const MyMentorsContext = createContext<MyMentorsContextProps>({
  mentors: [],
  mentorsLogos: {},
  isLoading: false,
  isEmptyState: false,
  selectedVenture: null,
  ventureOptions: [],
  isVenturesLoading: false,
  writeReviewTargetMentor: null,
  manageMentorTargetMentor: null,
  loadError: undefined,
  setMentors: () => {},
  setSelectedVenture: () => {},
  setWriteReviewTargetMentor: () => {},
  setManageMentorTargetMentor: () => {},
});

interface MyMentorsProviderProps {
  children: ReactNode;
}

export const MyMentorsProvider = ({ children }: MyMentorsProviderProps) => {
  const history = useHistory();
  const location = useLocation();
  const { get } = useQuery();

  const { identityid } = useContext(UserContext);

  const filterFromQuery: MyMentorsFilters = decodeQuery(get('filter') || '');

  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [mentorsLogos, setMentorsLogos] = useState<{
    [x: string]: string | null;
  }>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [isEmptyState, setIsEmptyState] = useState<boolean>(false);
  const [selectedVenture, setSelectedVenture] = useState<VentureId | null>(
    filterFromQuery?.venture || null,
  );
  const [ventureOptions, setVentureOptions] = useState<
    OptionProps<VentureId>[]
  >([]);
  const [isVenturesLoading, setIsVenturesLoading] = useState(true);
  const [writeReviewTargetMentor, setWriteReviewTargetMentor] =
    useState<Mentor | null>(null);
  const [manageMentorTargetMentor, setManageMentorTargetMentor] =
    useState<Mentor | null>(null);

  const errorHandler = (error: any) => {
    setIsLoading(false);
    if (error?.response?.status === 404 || error?.message === '404') {
      return setIsEmptyState(true);
    }
    const errorMessage =
      error?.response?.data?.message || 'Internal server error';
    setLoadError(errorMessage);
  };

  const loadMentorsLogos = useCallback(async (mentorsList: Mentor[]) => {
    try {
      const mentorImages = mentorsList.map((mentor: Mentor) => ({
        id: mentor.id,
        logo: mentor.logo,
      }));
      setMentorsLogos(() => {
        const sources = mentorImages.reduce((res, mentor) => {
          res[mentor.id] = mentor.logo ? '' : null;
          return res;
        }, {} as { [x: string]: string | null });
        return sources;
      });
      const shouldBeLoaded = mentorImages.filter(
        (mentorImage) => !!mentorImage.logo,
      );
      const logos = shouldBeLoaded.map((mentorImage) => mentorImage.logo);
      if (!logos.length) {
        return false;
      }
      const loadedLogos = await filesAPI.getFileLogos(logos as string[]);

      const nextState = shouldBeLoaded.reduce(
        (res, mentorImage, mentorImageIndex) => {
          res[mentorImage.id] = loadedLogos[mentorImageIndex];
          return res;
        },
        {} as { [x: string]: string },
      );
      return setMentorsLogos((prevMentors) => ({
        ...prevMentors,
        ...nextState,
      }));
    } catch (e: any) {
      console.log('loadMentorsLogos error');
      return false;
    }
  }, []);

  useEffect(() => {
    if (identityid) {
      const loadVentures = async (founderId: FounderId) => {
        try {
          setVentureOptions([]);
          setIsVenturesLoading(true);
          const loadedVentures =
            await venturesAPI.getVenturesDetailsByFounderId(founderId);
          const loadedVentureOptions: OptionProps<VentureId>[] =
            loadedVentures.map((v) => ({
              value: v.id,
              label: v.ventureName,
            }));
          setVentureOptions(loadedVentureOptions);
          setSelectedVenture((prev) =>
            prev || loadedVentures.length ? loadedVentures[0].id : null,
          );
          setIsVenturesLoading(false);
        } catch (error) {
          console.error(error);
          setIsVenturesLoading(false);
        }
      };

      loadVentures(identityid as FounderId);
    }
  }, [identityid, setSelectedVenture]);

  useEffect(() => {
    if (identityid && selectedVenture) {
      const loadMentors = async (ventureId: VentureId) => {
        try {
          setMentors([]);
          setIsLoading(true);
          setIsEmptyState(false);
          const loadedMentors = await mentorsAPI.getMentorsDetailsByVentureId(
            ventureId,
          );
          if (loadedMentors.length === 0) throw new Error('404');
          setMentors(loadedMentors);
          setIsLoading(false);
        } catch (error) {
          errorHandler(error);
        }
      };

      loadMentors(selectedVenture);
    }
  }, [identityid, selectedVenture, setMentors]);

  useEffect(() => {
    history.replace(
      `${location.pathname}?${stringifyQuery({
        filter: encodeQuery({
          venture: selectedVenture,
        }),
      })}`,
    );
  }, [history, location.pathname, selectedVenture]);

  useEffect(() => {
    if (mentors.length) {
      loadMentorsLogos(mentors);
    }
  }, [loadMentorsLogos, mentors]);

  const contextValue: MyMentorsContextProps = {
    mentors,
    mentorsLogos,
    isLoading,
    isEmptyState,
    selectedVenture,
    ventureOptions,
    isVenturesLoading,
    writeReviewTargetMentor,
    manageMentorTargetMentor,
    loadError,
    setMentors,
    setSelectedVenture,
    setWriteReviewTargetMentor,
    setManageMentorTargetMentor,
  };

  return (
    <MyMentorsContext.Provider value={contextValue}>
      {children}
    </MyMentorsContext.Provider>
  );
};

export const useMyMentorsContext = () => {
  const context = useContext(MyMentorsContext);
  if (!context) {
    throw new Error(
      'useMyMentorsContext must be used within a MyMentorsProvider',
    );
  }
  return context;
};
